import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { welcome } from '@magic/data/messages';
import type { PublicAgent } from '@magic/schemas/agents';
import type { ChatRequestInput as ChatRequest, ChatResponse } from '@magic/schemas/chat';
import type {
  Message,
  NewMessage,
  UpdateMessage,
} from '@magic/schemas/messages';
import { useLocalStorage } from '@magic/ui/hooks/use-local-storage';

import type { WithChildren } from '@exact-magic/react-utils';
import { request } from '../request';
import { useUTM } from '../utm';

export type UseChatOptions = {
  api?: string;
  preview?: boolean;
  body?: object;
  agent: PublicAgent;
  onError?: (error: Error) => void;
};

export type UseChatState = {
  id?: ChatRequest['id'];
  lead?: ChatResponse['lead'];
  status: ChatResponse['status'];
  messages: Message[];
  isLoading: boolean;
  error?: unknown;
  input: string;
};

export type UseChatHelpers = UseChatState & {
  append: (message: NewMessage) => Promise<void>;
  handleInputChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  handleSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  reset: () => void;
  handleFeedback: (feedback: UpdateMessage) => void;
};

const initialState = (agent: PublicAgent): UseChatState => ({
  id: undefined,
  lead: undefined,
  messages: [
    // @ts-ignore
    {
      id: 'msg_welcome',
      role: 'assistant',
      content: agent.welcome || welcome,
      createdAt: new Date(),
      metadata: {
        welcome: true,
      },
    },
  ],
  status: 'active',
  isLoading: false,
  error: undefined,
  input: '',
});

type Action<P = void> = (payload: P) => (state: UseChatState) => UseChatState;

const sendMessage: Action<NewMessage> = (message) => (state) => ({
  ...state,
  input: '',
  isLoading: true,
  messages: [message as Message, ...state.messages],
});

const receiveReply: Action<ChatResponse> = (data) => (state) => ({
  ...state,
  id: data.id,
  isLoading: false,
  lead: state.lead || data.lead,
  messages: [...data.messages.reverse(), ...state.messages],
});

const finishConversation: Action = () => (state) => ({
  ...state,
  status: 'finished',
});

const resetConversation: Action<PublicAgent> = (agent) => () =>
  initialState(agent);

const setInput = (input: string) => (state: UseChatState) => ({
  ...state,
  input,
});

const handleError = (error: unknown) => (state: UseChatState) => ({
  ...state,
  error,
  isLoading: false,
});

const updateMessages: Action<Message> = (feedback) => (state) => ({
  ...state,
  messages: state.messages.map((message) =>
    message.id === feedback.id
      ? { ...message, feedback: feedback.feedback }
      : message
  ),
});

const expectedFinishDate = (message?: Message) => {
  if (!message || message.metadata?.welcome) return null;
  const createdAt = message.createdAt
    ? new Date(message.createdAt)
    : new Date();
  const when = new Date(createdAt).getTime() + 3600000 - new Date().getTime();
  return when > 0 ? when : 0;
};

function useFinishConversation(
  callback: () => void,
  status: ChatResponse['status'],
  message?: Message
) {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  return useEffect(() => {
    const delay = expectedFinishDate(message);

    if (delay !== null) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        if (status !== 'finished') callback();
      }, delay);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [message?.createdAt, message?.metadata?.welcome, callback]);
}

export const ChatContext = createContext<UseChatHelpers>(undefined!);

export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) throw new Error('useChat must be used inside an ChatProvider');
  return context;
};

export type ChatProviderProps = WithChildren<UseChatOptions>;

export const ChatProvider = forwardRef<UseChatHelpers, ChatProviderProps>(
  (
    { api = '/api/chat', preview = false, body = {}, agent, onError, children },
    ref
  ) => {
    const key = `chat_${agent.id}`;
    const [state, setState] = useLocalStorage(
      preview ? `preview_${key}` : key,
      initialState(agent)
    );

    const utm = useUTM({ agent });

    const append = useCallback(
      async (message: NewMessage): Promise<void> => {
        setState(sendMessage(message));

        try {
          const data = await request<ChatResponse, ChatRequest>({
            api: `${api}/${agent.workspaceId}/${agent.id}`,
            method: 'POST',
            body: {
              ...body,
              messages: [message],
              tools: [],
              format: 'markdown',
              utm,
              preview,
              id: state.id,
            },
          });

          setState(receiveReply(data));
        } catch (error) {
          setState(handleError(error));
          if (onError) {
            onError(error as Error);
          }
        }
      },
      [api, agent, preview, state.id, onError]
    );

    const handleSubmit = useCallback(
      (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        append({ role: 'user', content: state.input, createdAt: new Date() });
      },
      [state.input, append]
    );

    const handleFeedback = async (feedback: UpdateMessage): Promise<void> => {
      try {
        const data = await request<Message, UpdateMessage>({
          api: `${api}/${agent.workspaceId}/${agent.id}/message/feedback`,
          method: 'POST',
          body: feedback,
        });

        setState(updateMessages(data));
      } catch (error) {
        console.log(error);

        if (onError) {
          onError(error as Error);
        }
      }
    };

    const handleInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setState(setInput(e.target.value));
      },
      []
    );

    const reset = useCallback(() => {
      setState(resetConversation(agent));
    }, [agent]);

    const finish = useCallback(() => {
      setState(finishConversation());
    }, []);

    useFinishConversation(
      finish,
      state.status,
      state.messages.find((m) => m.role === 'assistant')
    );

    const helpers = useMemo(
      () => ({
        ...state,
        append,
        handleInputChange,
        handleSubmit,
        reset,
        handleFeedback,
      }),
      [state, append, handleInputChange, handleSubmit, reset, handleFeedback]
    );

    useImperativeHandle(ref, () => helpers, [helpers]);

    return (
      <ChatContext.Provider value={helpers}>{children}</ChatContext.Provider>
    );
  }
);
